// Inline practice (block_type "inline_quiz") on notebook reader pages.
// Server renders the full card (pages/_block.html.erb); this module only adds
// behaviour, mirroring the questions#show OMR card: pick an option → correct
// marks and auto-reveals the answer; wrong marks the pick but does NOT reveal
// (deliberate — same as questions#show). First pick per question is captured
// (anon-capable) and mirrored to GA4.

import { markPageDone } from './progress'

const TOKEN_KEY = 'ab_anon_token'

function anonToken () {
    try {
        let token = localStorage.getItem(TOKEN_KEY)
        if (!token || !/^[A-Za-z0-9_-]{8,64}$/.test(token)) {
            token = Array.from(crypto.getRandomValues(new Uint8Array(16)),
                b => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-'[b & 63]).join('')
            localStorage.setItem(TOKEN_KEY, token)
        }
        return token
    } catch (e) {
        return null // storage blocked — attempt still counts via GA
    }
}

function csrfToken () {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : null
}

function gaEvent (name, params) {
    if (typeof window.gtag === 'function') window.gtag('event', name, params)
}

function capture (payload) {
    const token = csrfToken()
    fetch('/api/v1/lesson_attempts', {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(token ? { 'X-CSRF-Token': token } : {})
        },
        body: JSON.stringify({ lesson_attempt: payload })
    }).catch(() => {}) // fire-and-forget: practice UX never blocks on capture
}

// A page can carry more than one inline_quiz block (e.g. "Set 1"/"Set 2");
// the page only counts as practiced once every question in every block has
// been attempted, not just the first one anywhere on the page.
function allQuestionsAttempted () {
    const questions = document.querySelectorAll('.nb-practice-q')
    return questions.length > 0 && Array.from(questions).every(q => q.dataset.attempted)
}

function setStatus (questionEl, text) {
    const status = questionEl.querySelector('[data-role="practice-status"]')
    if (status) status.textContent = text
}

function setWrongMessage (questionEl, show) {
    const msg = questionEl.querySelector('.nb-practice-wrong-msg')
    if (msg) msg.hidden = !show
}

function revealAnswer (questionEl, show) {
    const answer = questionEl.querySelector('.nb-practice-answer')
    const reveal = questionEl.querySelector('.nb-practice-reveal')
    if (!answer) return
    answer.hidden = !show
    if (reveal) reveal.textContent = show ? 'Hide answer' : 'Reveal answer'
    const correctOption = questionEl.querySelector('.nb-practice-option[data-correct]')
    if (correctOption) correctOption.classList.toggle('show-correct', show)
}

function handleOption (option) {
    const questionEl = option.closest('.nb-practice-q')
    const practice = option.closest('.nb-practice')
    if (!questionEl || questionEl.dataset.solved) return

    const correct = option.dataset.correct === '1'

    if (!questionEl.dataset.attempted) {
        questionEl.dataset.attempted = '1'
        const params = {
            page_id: practice ? practice.dataset.pageId : null,
            question_id: questionEl.dataset.questionId,
            status: correct ? 'correct' : 'wrong'
        }
        gaEvent('lesson_practice_attempt', params)
        // Practicing completes the lesson (plan §6) — but only once every
        // question in every practice block on the page has been attempted,
        // not just the first one. Members are recorded server-side by the
        // attempt capture; this mirrors for guests + paints the checkmark.
        if (practice && allQuestionsAttempted()) markPageDone(practice.dataset.pageId)
        capture({
            block_id: practice ? practice.dataset.blockId : null,
            question_id: questionEl.dataset.questionId,
            answer_id: option.dataset.answerId,
            anon_token: anonToken()
        })
    }

    if (correct) {
        option.classList.add('is-correct')
        questionEl.dataset.solved = '1'
        questionEl.querySelectorAll('.nb-practice-option').forEach(el => el.classList.add('is-done'))
        setWrongMessage(questionEl, false)
        setStatus(questionEl, 'Correct.')
        revealAnswer(questionEl, true)
    } else {
        option.classList.add('is-wrong', 'is-done')
        setWrongMessage(questionEl, true)
        setStatus(questionEl, 'Incorrect. Not quite — take another look, or reveal the answer below.')
        if (navigator.vibrate) navigator.vibrate(150)
    }
}

document.addEventListener('click', (event) => {
    const option = event.target.closest('.nb-practice-option')
    if (option) return handleOption(option)

    const reveal = event.target.closest('.nb-practice-reveal')
    if (reveal) {
        const questionEl = reveal.closest('.nb-practice-q')
        const answer = questionEl && questionEl.querySelector('.nb-practice-answer')
        if (answer) revealAnswer(questionEl, answer.hidden)
        return
    }

    const cta = event.target.closest('.nb-practice-cta')
    if (cta) {
        let params = {}
        try { params = JSON.parse(cta.dataset.gaParams || '{}') } catch (e) { /* noop */ }
        gaEvent(cta.dataset.gaEvent || 'lesson_full_test_click', params)
    }
})
